import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { LayoutService } from "../../services/layout.service";
import { filter, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-profile-feature-banner",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./profile-feature-banner.component.html",
})
export class ProfileFeatureBannerComponent implements OnInit, OnDestroy {
  isOpen = false;

  private readonly storageKey = "dj_profile_feature_banner_v1_dismissed";
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    public authService: AuthService,
    private layoutService: LayoutService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    this.updateVisibility();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateVisibility());

    this.layoutService.isPublicProfilePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateVisibility());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Mark banner as interacted so it is not shown again. */
  private markInteracted(): void {
    this.isOpen = false;
    if (this.isBrowser) {
      localStorage.setItem(this.storageKey, "1");
    }
  }

  dismiss(): void {
    this.markInteracted();
  }

  learnMore(): void {
    this.markInteracted();
    const target = "/profile";
    if (this.authService.isAuthenticated()) {
      this.router.navigate([target]).then(() => window.scrollTo(0, 0));
      return;
    }
    this.authService.setRedirectPath(target);
    this.authService.openLoginModal();
  }

  viewExample(): void {
    if (this.isBrowser) {
      window.open("https://www.designjobs.careers/carlos", "_blank", "noopener,noreferrer");
    }
  }

  private updateVisibility(): void {
    if (!this.isBrowser) return;

    const dismissed = localStorage.getItem(this.storageKey) === "1";
    const url = this.router.url.split("?")[0];
    const hideOnRoute =
      url === "/profile" ||
      url.startsWith("/talent/") ||
      url === "/ai-job-matcher" ||
      this.layoutService.isPublicProfilePage;

    this.isOpen = !dismissed && !hideOnRoute;
  }
}

